import { StatusProxy, ToolBarItemType, type ToolBarItem } from "@focus/focusUI";
import { useState } from "react";
import { usePhonebook } from "../../hooks/usePhonebook";
import type { Contact, Organization } from "../../types/phonebook";
import { sortContactsByName } from "../../utils/phonebook";
import { Table } from "../table/Table";
import { TableShell } from "../table/TableShell";
import { CreateContactButton } from "./CreateContactButton";
import { CreateOrganizationButton } from "./CreateOrganizationButton";
import { DeleteContactButton } from "./DeleteContactButton";
import { DeleteOrganizationButton } from "./DeleteOrganizationButton";
import { EditContactButton } from "./EditContactButton";
import { EditOrganizationButton } from "./EditOrganizationButton";
import {
  PhonebookBooleanCell,
  PhonebookContactCell,
  PhonebookEmailCell,
  PhonebookNotesCell,
  PhonebookOrganizationCell,
  PhonebookPhoneCell,
  PhonebookTextCell,
} from "./PhonebookTableCells";

enum RowType {
  Organization = "Organization",
  Contact = "Contact",
}

const EditButton = ({ row, rowType }: { row; rowType: RowType }) => {
  if (row?.isUnassigned) return null;

  switch (rowType) {
    case RowType.Organization:
      return <EditOrganizationButton organization={row as Organization} />;
    case RowType.Contact:
      return <EditContactButton contact={row as Contact} />;
    default:
      return <EditContactButton contact={row as Contact} disabled />;
  }
};

const DeleteButton = ({ row, rowType }: { row; rowType: RowType }) => {
  if (row?.isUnassigned) return null;

  switch (rowType) {
    case RowType.Organization:
      return <DeleteOrganizationButton organization={row as Organization} />;
    case RowType.Contact:
      return <DeleteContactButton contact={row as Contact} />;
    default:
      return <DeleteContactButton contact={row as Contact} disabled />;
  }
};

export const PhonebookTable = () => {
  const { contacts, organizations, getRequestStatus, getRequestError } =
    usePhonebook();

  const [rowSelectionState, setRowSelectionState] = useState<
    Record<string, boolean>
  >({});

  const selectedRowId = Object.keys(rowSelectionState).find(
    (key) => rowSelectionState[key],
  );

  let selectedRow = null;
  let selectedRowType: RowType | null = null;
  if (selectedRowId) {
    selectedRow = contacts.find((c) => String(c.id) === selectedRowId);
    if (selectedRow) {
      selectedRowType = RowType.Contact;
    } else {
      selectedRow = organizations.find((o) => String(o.id) === selectedRowId);
      if (selectedRow) {
        selectedRowType = RowType.Organization;
      }
    }
  }

  const columnDefs = [
    {
      headerName: "Organization",
      field: "organization",
      key: "organization",
      minSize: 320,
      size: 360,
      customCellRenderer: PhonebookOrganizationCell,
    },
    {
      headerName: "Organization Role",
      field: "orgRole",
      key: "orgRole",
      minSize: 160,
      size: 170,
      customCellRenderer: PhonebookBooleanCell,
    },
    {
      headerName: "SDA Member",
      field: "member",
      key: "member",
      minSize: 140,
      size: 150,
      customCellRenderer: PhonebookBooleanCell,
    },
    {
      headerName: "Name",
      field: "contactName",
      key: "contactName",
      hide: false,
      minSize: 260,
      size: 300,
      customCellRenderer: PhonebookContactCell,
    },
    {
      headerName: "Role",
      field: "role",
      key: "role",
      minSize: 160,
      size: 190,
      customCellRenderer: PhonebookTextCell,
    },

    {
      headerName: "Phone",
      field: "phoneNumber",
      key: "phoneNumber",
      minSize: 160,
      size: 180,
      customCellRenderer: PhonebookPhoneCell,
    },
    {
      headerName: "Email",
      field: "email",
      key: "email",
      minSize: 240,
      size: 280,
      customCellRenderer: PhonebookEmailCell,
    },
    {
      headerName: "Notes",
      field: "notes",
      key: "notes",
      minSize: 260,
      size: 320,
      customCellRenderer: PhonebookNotesCell,
    },
  ];

  const serverDisplayConfig = columnDefs.reduce(
    (acc, c, i) => ({
      ...acc,
      [c.key]: { visible: true, order: i, width: c.size ?? c.minSize ?? 180 },
    }),
    {},
  );

  function groupByNameAsArray(items: Contact[], organizations: Organization[]) {
    const UNASSIGNED_ORG = "Others";

    const organizationMap = new Map(
      organizations.map((org) => [org.name, org]),
    );

    const map = new Map<string, Contact[]>(
      organizations.map((org) => [org.name, []]),
    );

    if (!map.has(UNASSIGNED_ORG)) {
      map.set(UNASSIGNED_ORG, []);
    }

    for (const item of items) {
      const orgNames = item.organizations ?? [];

      // Only organizations that actually exist in the phonebook.
      const validOrgNames = orgNames.filter((name) =>
        organizationMap.has(name),
      );

      // Contacts without a valid organization are grouped under Others.
      if (validOrgNames.length === 0) {
        map.get(UNASSIGNED_ORG)!.push(item);
        continue;
      }

      for (const orgName of validOrgNames) {
        map.get(orgName)!.push(item);
      }
    }

    if ((map.get(UNASSIGNED_ORG)?.length ?? 0) === 0) {
      map.delete(UNASSIGNED_ORG);
    }

    return Array.from(map.entries())
      .map(([organizationName, items]) => {
        const organization = organizationMap.get(organizationName);

        return {
          ...organization,
          organization: organizationName,
          isUnassigned: organizationName === UNASSIGNED_ORG,
          subRows: sortContactsByName(items),
        };
      })
      .sort((a, b) => {
        if (a.isUnassigned) return 1;
        if (b.isUnassigned) return -1;

        return a.organization.localeCompare(b.organization, undefined, {
          sensitivity: "base",
        });
      });
  }

  const status = getRequestStatus(["getOrganizations", "getContacts"]);
  const error = getRequestError(["getOrganizations", "getContacts"]);

  const leftMenuItems: ToolBarItem<ToolBarItemType.Custom>[] = [
    {
      type: ToolBarItemType.Custom,
      component: <EditButton row={selectedRow} rowType={selectedRowType} />,
    },
    {
      type: ToolBarItemType.Custom,
      component: <DeleteButton row={selectedRow} rowType={selectedRowType} />,
    },
    {
      type: ToolBarItemType.Custom,
      component: <CreateContactButton />,
    },
    {
      type: ToolBarItemType.Custom,
      component: <CreateOrganizationButton />,
    },
  ];

  return (
    <div className="h-full min-h-0">
      <StatusProxy status={status} error={error}>
        <TableShell className="phonebook-table-shell">
          <Table
            columnDefinitions={columnDefs}
            serverDisplayConfig={serverDisplayConfig}
            defaultVisibleValues={columnDefs.map((c) => c.key)}
            rowData={groupByNameAsArray(contacts, organizations)}
            base={"phonebook"}
            height="auto"
            showGlobalFilter
            rowClassRules={{
              "!bg-slate-50": (row) =>
                row.depth === 0 && selectedRowId !== String(row.original?.id),
              "!font-semibold": (row) => row.depth === 0,
              "!bg-amber-50": (row) => row.original?.isUnassigned,
              "!bg-sky-50": (row) => selectedRowId === String(row.original?.id),
            }}
            controlledRowSelection={rowSelectionState}
            controlledRowSelectionChange={setRowSelectionState}
            enableMultiSelection={false}
            leftMenuItems={leftMenuItems}
          />
        </TableShell>
      </StatusProxy>
    </div>
  );
};
